/**
 * Padel USA — <campaign-slideshow> for sections/homepage-campaign_custom.liquid (Version D).
 * Scroll-snap track (native swipe), dots, pause/play, autoplay that pauses on hover, keyboard
 * focus, hidden tab and user pause, and never starts under prefers-reduced-motion.
 * Inactive slides are `inert` so Tab only reaches the visible slide.
 * Dossier: custom-code-padelusa/features/homepage-recommended-order.md
 */
if (!customElements.get('campaign-slideshow')) {
  class CampaignSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.pauseButton = this.querySelector('[data-pause]');
      this.index = 0;
      this.delay = parseFloat(this.dataset.autoplay || '0') * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = this.reducedMotion;
      this.hovered = false;
      this.focused = false;
      if (this.slides.length < 2) return;

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.goTo(i); this.restart(); }));
      this.pauseButton?.addEventListener('click', () => this.setUserPaused(!this.userPaused));
      this.addEventListener('mouseenter', () => { this.hovered = true; });
      this.addEventListener('mouseleave', () => { this.hovered = false; });
      // Only keyboard focus pauses; a mouse click on a dot must not stop autoplay for good.
      this.addEventListener('focusin', (event) => { this.focused = event.target.matches(':focus-visible'); });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.focused = false;
      });

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.setActive(this.slides.indexOf(entry.target));
          }
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((slide) => this.observer.observe(slide));

      this.setUserPaused(this.userPaused);
      this.restart();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.observer?.disconnect();
    }

    setActive(index) {
      if (index < 0 || index === this.index) return;
      this.index = index;
      this.slides.forEach((slide, i) => { slide.inert = i !== index; });
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    goTo(index) {
      const target = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      this.setActive(target);
    }

    setUserPaused(paused) {
      this.userPaused = paused;
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.setAttribute('aria-label', paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause);
    }

    restart() {
      clearInterval(this.timer);
      if (!this.delay) return;
      this.timer = setInterval(() => {
        if (this.userPaused || this.hovered || this.focused || document.hidden) return;
        this.goTo(this.index + 1);
      }, this.delay);
    }
  }
  customElements.define('campaign-slideshow', CampaignSlideshow);
}
